import { Injectable } from '@angular/core';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  CollectionReference,
  DocumentData,
  onSnapshot,
  arrayUnion,
  limit
} from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { UserInteractionService } from './user-interaction.service';
import { environment } from 'env/environment';
import { Observable } from 'rxjs';
import { isIndependentNoAircraft } from 'app/utils/membership-utils';

export interface Notification {
  createdAt: Date;
  emailedOn: Date | null;
  watcherId: string | null;
  type: string;
  connectionId: string;
  notificationFlightOptions: any | null;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class FlightConnectionsService {
  private connectionsRef: CollectionReference<DocumentData>;

  constructor(
    private firebaseService: FirebaseService,
    private userService: UserService,
    private userInteraction: UserInteractionService
  ) {
    this.connectionsRef = collection(this.firebaseService.firestore, 'flight-connections');
  }

  private async getUser() {
    const user = await this.userService.getUserProfile();
    if (!user) throw new Error('User not authenticated.');
    return user;
  }

  async createConnectionRequest(data: any): Promise<string> {
    const user = await this.getUser();
    const recipient = await this.userService.getProfileById(data.toId);
    const isBlocked = await this.userInteraction.isUserBlockedBy(data.toId);
    const hasBlocked = await this.userInteraction.hasCurrentUserBlocked(data.toId);
    const userInd = isIndependentNoAircraft(user.memberships);
    const recipInd = isIndependentNoAircraft(recipient?.memberships);
    if (isBlocked || hasBlocked || (userInd && recipInd)) {
      throw new Error('Connection request not allowed');
    }

    if (!data.requestMessage || !data.requestMessage.trim()) {
      throw new Error('Connection request message is required.');
    }

    const id = doc(this.connectionsRef).id;

    const {shareOfHours: _shareOfHours, ...cleanData} = data;

    const trimmedMessage = data.requestMessage.trim();

    const payload = {
      ...cleanData,
      requestMessage: trimmedMessage,
      id,
      createdBy: user.id,
      createdAt: new Date(),
      status: 'pending',
    };

    await setDoc(doc(this.connectionsRef, id), payload);

    const emailData = {
      toEmail: cleanData.toEmail,
      toUserId: cleanData.toId,
      fromDisplayName: cleanData.fromDisplayName,
      fromLocation: cleanData.fromLocation,
      toLocation: cleanData.toLocation,
      seatsRequested: cleanData.seatsRequested,
      connectionId: id,
      requestMessage: trimmedMessage,
    }

    await this.handleApiCall('/sendConnectionRequestEmail', emailData);

    return id;
  }

  async cancelConnectionRequest(requestId: string): Promise<void> {
    await deleteDoc(doc(this.connectionsRef, requestId));
  }

  async declineConnectionRequest(requestId: string, recipientUserId: string, recipientEmail: string, userDisplayName: string, routeDesc: string): Promise<void> {
    await updateDoc(doc(this.connectionsRef, requestId), {
      status: 'declined',
      updatedAt: new Date(),
      declinedAt: new Date(),
    });

    const emailData = {
      toEmail: recipientEmail,
      fromDisplayName: userDisplayName,
      friendlyRoute: routeDesc,
      connectionId: requestId,
    };

    await this.handleApiCall('/sendConnectionDeclinedEmail', emailData);

    const notification: Notification = {
      createdAt: new Date(),
      emailedOn: null,
      watcherId: null,
      type: 'connection-declined',
      connectionId: requestId,
      notificationFlightOptions: null,
      message: `${userDisplayName} declined your request for the flight from ${routeDesc}.`,
    };

    const notifRef = doc(collection(this.firebaseService.firestore, `users/${recipientUserId}/notifications`));
    await setDoc(notifRef, notification);
  }

  async acceptConnectionRequest(requestId: string, recipientUserId: string, recipientEmail: string, userDisplayName: string, routeDesc: string): Promise<void> {
    await updateDoc(doc(this.connectionsRef, requestId), {
      status: 'accepted',
      updatedAt: new Date(),
      acceptedAt: new Date(),
    });

    const emailData = {
      toEmail: recipientEmail,
      fromDisplayName: userDisplayName,
      friendlyRoute: routeDesc,
      connectionId: requestId,
    }

    await this.handleApiCall('/sendConnectionAcceptedEmail', emailData);

    // Create user-level notification
    const notification: Notification = {
      createdAt: new Date(),
      emailedOn: null,
      watcherId: null,
      type: 'connection-accepted',
      connectionId: requestId,
      notificationFlightOptions: null,
      message: `${userDisplayName} accepted your request for the flight from ${routeDesc}.`,
    };

    const notifRef = doc(collection(this.firebaseService.firestore, `users/${recipientUserId}/notifications`));
    await setDoc(notifRef, notification);
  }

  async updateConnection(connectionId: string, updates: Partial<any>): Promise<void> {
    await updateDoc(doc(this.connectionsRef, connectionId), {
      ...updates,
      updatedAt: new Date(),
    });
  }

  async cancelConnection(connectionId: string, userNameThatCanceled: string, recipientUserId: string, routeDesc: string): Promise<void> {
    await updateDoc(doc(this.connectionsRef, connectionId), {
      status: 'canceled',
      canceledAt: new Date(),
    });
    // Create user-level notification
    const notification: Notification = {
      createdAt: new Date(),
      emailedOn: null,
      watcherId: null,
      type: 'connection-canceled',
      connectionId: connectionId,
      notificationFlightOptions: null,
      message: `${userNameThatCanceled} canceled and left your connection - ${routeDesc}.`,
    };

    const notifRef = doc(collection(this.firebaseService.firestore, `users/${recipientUserId}/notifications`));
    await setDoc(notifRef, notification);
  }

  async markPhoneNumberShared(connectionId: string, userId: string): Promise<void> {
    await updateDoc(doc(this.connectionsRef, connectionId), {
      phoneSharedUsers: arrayUnion(userId),
      updatedAt: new Date(),
    });
  }

  async getConnectionByUserId(userId: string): Promise<any[]> {
    const fromQuery = query(this.connectionsRef, where('fromId', '==', userId));
    const toQuery = query(this.connectionsRef, where('toId', '==', userId));

    const [fromSnap, toSnap] = await Promise.all([
      getDocs(fromQuery),
      getDocs(toQuery)
    ]);

    const combinedDocs = [...fromSnap.docs, ...toSnap.docs];

    // Remove duplicates if any (e.g., fromId === toId)
    const uniqueMap = new Map<string, any>();
    combinedDocs.forEach(doc => {
      uniqueMap.set(doc.id, { id: doc.id, ...doc.data() });
    });

    return Array.from(uniqueMap.values());
  }

  async hasConnectionWithMinMessages(userId: string, minMessages: number): Promise<boolean> {
    const connections = await this.getConnectionByUserId(userId);
    for (const conn of connections) {
      const messagesRef = collection(this.firebaseService.firestore, `flight-connections/${conn.id}/messages`);
      const q = query(messagesRef, limit(minMessages + 1));
      const snap = await getDocs(q);
      if (snap.size > minMessages) {
        return true;
      }
    }
    return false;
  }

  async getConnectionById(id: string): Promise<any | null> {
    const docRef = doc(this.connectionsRef, id);
    const snapshot = await getDoc(docRef);

    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }

  observeConnectionById(id: string): Observable<any | null> {
    return new Observable((observer) => {
      const docRef = doc(this.connectionsRef, id);

      const unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (snapshot.exists()) {
          observer.next({ id: snapshot.id, ...snapshot.data() });
        } else {
          observer.next(null);
        }
      }, (error) => {
        console.error('Firestore listen error:', error);
        observer.error(error);
      });

      // Cleanup on unsubscribe
      return () => unsubscribe();
    });
  }

  async getConnectionRequestsByFlightOptionId(optionId: string): Promise<any[]> {
    const q = query(this.connectionsRef, where('toUserFlightOptionId', '==', optionId));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  private async handleApiCall(endpoint: string, payload: any) {
    const url = `https://${environment.apiBaseURL}.cloudfunctions.net${endpoint}`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`API error (${endpoint}): ${res.status} - ${err}`);
      }
    } catch (error) {
      console.error(`API call to ${endpoint} failed:`, error);
    }
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  async checkFlightOptionForExistingConnection(id: string): Promise<string | null> {
    const user = await this.getUser();
    const fromQuery = query(this.connectionsRef, where('fromId', '==', user.id));
    const toQuery = query(this.connectionsRef, where('toId', '==', user.id));

    const [fromSnap, toSnap] = await Promise.all([
      getDocs(fromQuery),
      getDocs(toQuery)
    ]);

    const combinedDocs = [...fromSnap.docs, ...toSnap.docs];
    const uniqueMap = new Map<string, any>();
    combinedDocs.forEach(doc => {
      uniqueMap.set(doc.id, { id: doc.id, ...doc.data() });
    });
    const matches = Array.from(uniqueMap.values());
    const matchingOption = matches.find((d) => d.toUserFlightOptionId === id || d.fromUserFlightOptionId === id);
    if (matchingOption) {
      return matchingOption.status;
    } else {
      return null;
    }
  }

  private isActiveRequestStatus(status: unknown): boolean {
    if (typeof status !== 'string') {
      return true;
    }

    const normalizedStatus = status.toLowerCase();

    // Users should only be able to send a follow up request when they explicitly cancel.
    // Declined requests should still block the user from sending another request.
    return normalizedStatus !== 'canceled';
  }

  async checkHasUserRequestedOption(optionId: string, targetUserId?: string): Promise<boolean> {
    const user = await this.getUser();

    const sentRequestsSnapshot = await getDocs(
      query(this.connectionsRef, where('fromId', '==', user.id))
    );

    return sentRequestsSnapshot.docs.some((docSnap) => {
      const data = docSnap.data();
      if (!this.isActiveRequestStatus(data?.status)) {
        return false;
      }

      const matchesOption = Boolean(optionId) && data?.toUserFlightOptionId === optionId;
      const matchesTargetUser =
        Boolean(targetUserId) &&
        data?.toId === targetUserId &&
        (!data?.toUserFlightOptionId || data?.toUserFlightOptionId === optionId);

      return matchesOption || matchesTargetUser;
    });
  }


}
